/**
 * Advanced Exit (OCO) builder — Directive 13.
 *
 * Controlled component: the parent (Dashboard.jsx / InstrumentInspector.jsx)
 * owns `draft` (absolute prices) so the same values can drive both this form
 * and any chart-sync UI. Deep-linkable via `?order=advanced-oco&step=…` URL
 * state (set by the parent when this panel opens/closes).
 *
 * Feature gate: full create access is Premium (Advanced Trader) /
 * Institutional / active Obondo trial (`canCreate`). Free tier sees an
 * inline upgrade callout instead of the creation tool — existing OCO
 * orders elsewhere in the app are never revoked.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GitBranch, Target, ShieldAlert, Receipt, Lock } from "lucide-react";

const FEE_TABS = [7, 30, 60, 90];

export function AdvancedExitPanel({ open, position, currentPrice, draft, onDraftChange, onClose, onCreated, canCreate }) {
    const { currency, formatMoney, rates } = useCurrency();
    const [tpMode, setTpMode] = useState("pct"); // 'pct' | 'fiat'
    const [tpInput, setTpInput] = useState("10");
    const [slMode, setSlMode] = useState("fiat"); // 'pct' | 'fiat'
    const [slInput, setSlInput] = useState("");
    const [trailInput, setTrailInput] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [feeDays, setFeeDays] = useState(30);
    const [feeSummary, setFeeSummary] = useState(null);

    // Sync local inputs -> lifted `draft` (absolute KES prices, since that's
    // what /oco/create expects) for chart sync. Fiat-mode inputs are typed
    // in the trader's selected display currency and converted to KES here —
    // previously they were sent to the backend completely unconverted
    // (a USD "25" would silently become a KES 25 stop, off by ~130x).
    useEffect(() => {
        if (!open || !currentPrice) return;
        const toKES = (v) => (currency === "KES" ? Number(v) : Number(v) * (rates[currency] || 1));
        const tp = tpMode === "pct"
            ? (tpInput ? currentPrice * (1 + Number(tpInput) / 100) : null)
            : (tpInput ? toKES(tpInput) : null);
        const fixed = slMode === "pct"
            ? (slInput ? currentPrice * (1 - Number(slInput) / 100) : null)
            : (slInput ? toKES(slInput) : null);
        const trailFloor = trailInput ? currentPrice * (1 - Number(trailInput) / 100) : null;
        onDraftChange?.({
            takeProfit: tp ? Number(tp.toFixed(2)) : null,
            fixedStop: fixed ? Number(fixed.toFixed(2)) : null,
            trailingFloor: trailFloor ? Number(trailFloor.toFixed(2)) : null,
            trailingPct: trailInput ? Number(trailInput) : null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tpMode, tpInput, slMode, slInput, trailInput, currentPrice, currency, rates]);

    useEffect(() => {
        if (open) {
            api.get(`/fees/summary?days=${feeDays}`).then(({ data }) => setFeeSummary(data)).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, feeDays]);

    if (!open || !position) return null;

    const entry = Number(position.entry_price || 0);
    const qty = Number(position.qty || 0);
    const tpAbs = draft?.takeProfit;
    const estProfit = tpAbs ? Math.round((tpAbs - entry) * qty) : null;
    const slAbs = draft?.fixedStop;
    const estLoss = slAbs ? Math.round((entry - slAbs) * qty) : null;

    async function submit() {
        if (!draft?.takeProfit && !draft?.fixedStop && !draft?.trailingPct) {
            toast.error("Add at least one leg — Take Profit, Fixed Stop, or Trailing Stop.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/oco/create", {
                position_id: position.id,
                take_profit_price: draft?.takeProfit || undefined,
                fixed_stop_price: draft?.fixedStop || undefined,
                trailing_stop_pct: draft?.trailingPct || undefined,
            });
            toast.success(`Advanced Exit strategy placed on ${position.symbol}.`);
            onCreated?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.reason || e?.response?.data?.detail || "Couldn't place the OCO order.");
        } finally { setSubmitting(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="advanced-exit-panel">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <GitBranch size={18} className="text-primary" /> Advanced Exit · {position.symbol}
                    </DialogTitle>
                </DialogHeader>

                {!canCreate ? (
                    <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm space-y-3" data-testid="oco-upgrade-callout">
                        <div className="flex items-center gap-2 font-semibold"><Lock size={16} className="text-primary" /> Advanced Exit is a Premium feature</div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            Take-profit + trailing-stop OCO strategies are available on the Advanced Trader / Institutional plans, or during your Obondo trial. Any existing OCO orders on your account keep working.
                        </p>
                        <a href="/app/subscription" className="inline-block text-xs font-bold px-3 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition" data-testid="oco-upgrade-cta">
                            View plans
                        </a>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Take Profit */}
                        <div className="rounded-lg border border-border p-3.5" data-testid="oco-tp-card">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5"><Target size={13} className="text-emerald-500" /> Profit target (sell-limit)</div>
                                <div className="flex text-[10px] rounded-md border border-border overflow-hidden">
                                    <button onClick={() => setTpMode("pct")} className={`px-2 py-1 min-h-[28px] ${tpMode === "pct" ? "bg-primary text-primary-foreground" : ""}`} data-testid="tp-mode-pct">%</button>
                                    <button onClick={() => setTpMode("fiat")} className={`px-2 py-1 min-h-[28px] ${tpMode === "fiat" ? "bg-primary text-primary-foreground" : ""}`} data-testid="tp-mode-fiat">{currency}</button>
                                </div>
                            </div>
                            <input
                                type="number" min="0" value={tpInput} onChange={(e) => setTpInput(e.target.value)}
                                className="w-full min-h-[44px] px-3 rounded-md border border-border bg-background text-sm mono"
                                placeholder={tpMode === "pct" ? "e.g. 10 (%)" : `e.g. 25.00 (${currency})`}
                                data-testid="tp-input"
                            />
                            {tpAbs > 0 && (
                                <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5 font-medium" data-testid="tp-subtext">
                                    Triggers at {formatMoney(tpAbs, "KES")} · est. profit +{formatMoney(estProfit, "KES")}
                                </div>
                            )}
                        </div>

                        {/* Safety Nets */}
                        <div className="rounded-lg border border-border p-3.5 space-y-3" data-testid="oco-safety-nets-card">
                            <div className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5"><ShieldAlert size={13} className="text-red-500" /> Safety nets (downside protection)</div>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-[11px] text-muted-foreground">Fixed stop-loss</label>
                                    <div className="flex text-[10px] rounded-md border border-border overflow-hidden">
                                        <button onClick={() => setSlMode("pct")} className={`px-2 py-1 min-h-[28px] ${slMode === "pct" ? "bg-primary text-primary-foreground" : ""}`} data-testid="sl-mode-pct">%</button>
                                        <button onClick={() => setSlMode("fiat")} className={`px-2 py-1 min-h-[28px] ${slMode === "fiat" ? "bg-primary text-primary-foreground" : ""}`} data-testid="sl-mode-fiat">{currency}</button>
                                    </div>
                                </div>
                                <input
                                    type="number" min="0" value={slInput} onChange={(e) => setSlInput(e.target.value)}
                                    className="w-full min-h-[44px] px-3 rounded-md border border-border bg-background text-sm mono"
                                    placeholder={slMode === "pct" ? "e.g. 5 (%)" : `e.g. 15.00 (${currency})`}
                                    data-testid="fixed-stop-input"
                                />
                                {slAbs > 0 && (
                                    <div className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium" data-testid="sl-subtext">
                                        Max loss ~{formatMoney(Math.abs(estLoss), "KES")}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-[11px] text-muted-foreground flex items-center gap-1">
                                    Trailing stop (%)
                                    <span className="text-[10px] text-muted-foreground/70" title="Non-reversing — the floor only ever moves up with the high-water mark, never back down.">
                                        (locks in gains — never loosens)
                                    </span>
                                </label>
                                <input
                                    type="number" min="0" max="90" value={trailInput} onChange={(e) => setTrailInput(e.target.value)}
                                    className="w-full min-h-[44px] px-3 rounded-md border border-border bg-background text-sm mono mt-1"
                                    placeholder="e.g. 5"
                                    data-testid="trailing-stop-input"
                                />
                                {draft?.trailingFloor > 0 && (
                                    <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium" data-testid="trailing-subtext">
                                        Current floor ~{formatMoney(draft.trailingFloor, "KES")} · rises with price, never falls
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Fee tracker */}
                        <div className="rounded-lg border border-border p-3.5" data-testid="fee-tracker-card">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5"><Receipt size={13} /> Transaction costs</div>
                                <div className="flex text-[10px] rounded-md border border-border overflow-hidden">
                                    {FEE_TABS.map((d) => (
                                        <button key={d} onClick={() => setFeeDays(d)} className={`px-2 py-1 min-h-[28px] ${feeDays === d ? "bg-primary text-primary-foreground" : ""}`} data-testid={`fee-tab-${d}`}>{d}d</button>
                                    ))}
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground">Flat 2.0% on every fill, across all settlement currencies.</div>
                            {feeSummary && (
                                <div className="text-sm font-bold mono mt-1.5" data-testid="fee-tracker-total">
                                    KES {feeSummary.total_fees_kes?.toLocaleString()} paid over {feeSummary.days} days ({feeSummary.trades_in_window} trades)
                                </div>
                            )}
                        </div>

                        {/* Summary */}
                        <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground leading-relaxed" data-testid="oco-summary-card">
                            {position.symbol}: sell {qty} units if price hits{" "}
                            {tpAbs ? <b className="text-emerald-600 dark:text-emerald-400">{formatMoney(tpAbs, "KES")} (profit target)</b> : "no profit target"},
                            {" "}or falls to{" "}
                            {slAbs ? <b className="text-red-600 dark:text-red-400">{formatMoney(slAbs, "KES")} (fixed stop)</b> : draft?.trailingFloor ? <b className="text-amber-600 dark:text-amber-400">the trailing floor</b> : "no stop"} — whichever comes first.
                        </div>

                        <button
                            onClick={submit}
                            disabled={submitting}
                            className="w-full min-h-[48px] text-sm font-bold px-4 py-3 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition disabled:opacity-50"
                            data-testid="place-oco-order-btn"
                        >
                            {submitting ? "Placing…" : "Place Advanced OCO Order"}
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default AdvancedExitPanel;
